import { useState, useEffect } from "react";
import { Dashicon } from "@wordpress/components";
import DeleteLayout from "./DeleteLayout";
import MoreOptions from "./MoreOptions";
import { useLayoutStatus } from "../LayoutStatusContext";
import { __ } from '@wordpress/i18n';
import { calendar, deleteIcon, infoIcon } from "../Icons";

const BoxPreview = ({ source, layout }) => {
  const [openMenuIndex, setOpenMenuIndex] = useState(null);
  const { getStatus, updateStatus } = useLayoutStatus();
  // State vars for Delete layout modal.
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const openDeleteModal                     = (id) => setDeleteTargetId(id);
  const closeDeleteModal                    = () => setDeleteTargetId(null);

  const handleEllipsisClick = (event, index) => {
    event.stopPropagation();
    setOpenMenuIndex(openMenuIndex === index ? null : index);
  };

  const closeMenu = () => {
    setOpenMenuIndex(null);
  };

  useEffect(() => {
    const handleClickOutside = () => closeMenu();
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  useEffect(() => {
    layout?.forEach((layout, index) => {
      const container = document.getElementById(
        `iframe-container-${source}-${index}`
      );
      if (container && !container.querySelector("iframe")) {
        const iframe = document.createElement("iframe");
        iframe.src = layout?.post_link || null;
        iframe.width = "100%";
        iframe.height = "100%";
        iframe.style.border = "none";
        const loader = document.getElementById(`iframe-container-${source}-${index}-loader`);
        if (loader) {
          loader.style.display = "flex"; // show loader
        }

        iframe.onload = () => {
          if (loader && container.contains(loader)) {
            loader.remove();
            const iframeDocument = iframe.contentDocument;
            if( iframeDocument ) {
              const adminBar = iframeDocument.getElementById("wpadminbar");
              const footer = iframeDocument.getElementById("footer");
              const header = iframeDocument.getElementById("masthead");
              const nav = iframeDocument.getElementsByClassName("navigation")[0];
              const sidebar = iframeDocument.getElementById("secondary");
              // Disable pointer events on the body to prevent interaction
              if (iframeDocument.body) {
                  iframeDocument.body.style.pointerEvents = "none";
              }
              if (adminBar) adminBar.remove();
              if (footer) footer.remove();
              if (header) header.remove();
              if (nav) nav.remove();
              if (sidebar) sidebar.remove();
            }
          }
        };
        container.appendChild(iframe);
      }

      // Disable if post_status is not 'publish'
      if (layout?.post_status !== 'publish') {
        updateStatus(layout.ID, 'disabled');
      }

    });
  }, [layout]);

  return (
    <div className="mt-8 grid grid-cols-3 gap-8">
      {layout.map((current, index) => (
        <div
          key={index}
          className="bg-white rounded-md relative group shadow-cardShadow"
        >
          <div className="flex p-6 justify-between items-center rounded-t-md">
            <div className="flex items-center gap-1.5">
              <div className={`w-1.5 h-1.5 rounded-full ${getStatus(current.ID, current.layout_status) ? 'bg-red-600' : 'bg-green-600' }`}></div>
              <h3 className="m-0 text-sm text-center">
                {current?.post_title
                  ? current.post_title.length > 15
                    ? current.post_title.substring(0, 15) + "..."
                    : current.post_title
                  : __("(untitled)", "responsive-addons")}
                {"publish" !== current?.post_status && " (" + current?.post_status + ")"}
              </h3>
            </div>
            <div className="flex gap-3">
              <div className="relative group/info flex items-center justify-center">
                {/* Info Icon */}
                <span className="text-black">{infoIcon}</span>
                {/* Tooltip */}
                <div
                  className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 hidden group-hover/info:flex flex-col items-start gap-1 bg-white rounded-md shadow-md p-2.5 z-20 min-w-[130px] text-[12px] text-black">
                  <span className="absolute -bottom-1 left-1/2 -translate-x-1/2  w-2 h-2 bg-white rotate-45 -mt-2 shadow-[0px_4px_8px_0px_#0000001F]"></span>
                  {/* Author Row */}
                  <div className="flex items-center gap-2 mb-1">
                    <img
                      className="w-4 h-4 rounded-full"
                      src={current?.author_image}
                      alt="author"
                    />
                    <span>{current?.author_name}</span>
                  </div>
                  {/* Date Row */}
                  <div className="flex items-center gap-2">
                    {calendar}
                    <span>{current?.post_modified}</span>
                  </div>
                </div>
              </div>
              <button onClick={() => openDeleteModal(current?.ID)} className="flex items-center hover:text-red-600">
                {deleteIcon}
              </button>
              <button
                onClick={(e) => handleEllipsisClick(e, index)}
                className="flex items-center cursor-pointer w-5"
              >
                {openMenuIndex !== index && (
                  <Dashicon size={16} icon="ellipsis" className="rotate-90" />
                )}
                {openMenuIndex === index && <Dashicon icon="no-alt" />}
              </button>
            </div>
          </div>
            {openMenuIndex === index && (
              <MoreOptions
                postID={current?.ID}
                postLink={current?.post_link}
                index={index} layout={layout}
                layoutStatus={current?.layout_status}
                postStatus={current?.post_status}
              />
            )}
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none z-20 mt-16 mb-6">
            <button
              className="flex gap-2.5 justify-center items-center px-4 py-2 rounded bg-btnPrimary text-white hover:bg-sky-600 text-sm pointer-events-auto min-w-[130px]"
              onClick={() => window.open( current?.edit_post_link, "_blank" )}
            >
              <Dashicon icon="edit" /> {__('Edit', 'responsive-addons')}
            </button>
          </div>
          <div className="px-6 pb-6">
            <div className="w-full h-48 relative" id={`iframe-container-${source}-${index}`}>
              {/* Loader Spinner */}
              <div
                id={`iframe-container-${source}-${index}-loader`}
                className="absolute inset-0 flex items-center justify-center z-10 bg-white/80 pointer-events-none"
              >
                <div className="w-8 h-8 border-4 border-gray-300 border-t-blue-500 rounded-full animate-spin"></div>
              </div>
            </div>
          </div>
        </div>
      ))}
      { deleteTargetId && <DeleteLayout closeModal={closeDeleteModal} postID={deleteTargetId} /> }
    </div>
  );
};

export default BoxPreview;
